import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { CalendarDays, CalendarX2, Check, CalendarClock } from 'lucide-react';
import type { WorkCalendarToken } from '@sleed/bizcommerce-api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { SystemGrid, type SystemGridColumn } from '~/components/system/grid/system-grid';
import { serverMessage } from '~/lib/api/server-error';
import { useWorkCalendars, useWorkCalendarMutations } from '~/hooks/use-available-dates';
import { calendarToForm, type WorkCalendarFormValues } from '~/lib/schemas/available-date';
import { WorkCalendarEditDialog } from './work-calendar-edit-dialog';
import { WorkDaysSheet } from './work-days-sheet';
import { ExceptionDaysSheet } from './exception-days-sheet';

/** Available dates surface — work calendars (master) with per-calendar work-day + exception-day managers. */
export function AvailableDatesGridView() {
	const { t } = useTranslation();
	const { data, isLoading, isError, refetch } = useWorkCalendars();
	const { remove } = useWorkCalendarMutations();

	const [editOpen, setEditOpen] = useState(false);
	const [editing, setEditing] = useState<WorkCalendarFormValues | null>(null);
	const [deleteRow, setDeleteRow] = useState<WorkCalendarToken | null>(null);
	const [workDaysFor, setWorkDaysFor] = useState<WorkCalendarToken | null>(null);
	const [exceptionsFor, setExceptionsFor] = useState<WorkCalendarToken | null>(null);

	const columns = useMemo<SystemGridColumn<WorkCalendarToken>[]>(
		() => [
			{
				key: 'year',
				header: t('system.availableDates.columns.year'),
				width: 'minmax(0,1fr)',
				sortValue: c => c.year ?? 0,
				exportValue: c => c.year ?? 0,
				render: c => <span className="text-sm font-medium text-foreground">{c.year}</span>
			},
			{
				key: 'weeklyLimit',
				header: t('system.availableDates.columns.weeklyLimit'),
				width: '120px',
				align: 'center',
				sortValue: c => (c.wlmt ? 1 : 0),
				exportValue: c => (c.wlmt ? t('common.yes') : t('common.no')),
				render: c => (c.wlmt ? <Check size={16} className="text-success-fg" /> : <span className="text-foreground-disabled">—</span>)
			},
			{
				key: 'visible',
				header: t('system.availableDates.columns.visible'),
				width: '80px',
				align: 'center',
				sortValue: c => (c.iv ? 1 : 0),
				exportValue: c => (c.iv ? t('common.yes') : t('common.no')),
				render: c => (c.iv ? <Check size={16} className="text-success-fg" /> : <span className="text-foreground-disabled">—</span>)
			},
			{
				key: 'sort',
				header: t('system.availableDates.columns.sort'),
				width: '70px',
				align: 'right',
				sortValue: c => c.si ?? 0,
				exportValue: c => c.si ?? 0,
				render: c => <span className="text-sm tabular-nums text-foreground-muted">{c.si}</span>
			}
		],
		[t]
	);

	const confirmDelete = () => {
		if (!deleteRow?.id) return;
		remove.mutate(deleteRow.id, {
			onSuccess: () => {
				toast.success(t('common.delete'));
				setDeleteRow(null);
			},
			onError: err => toast.error(serverMessage(err))
		});
	};

	return (
		<>
			<SystemGrid<WorkCalendarToken>
				gridId="available-dates"
				title={t('system.availableDates.title')}
				rows={data ?? []}
				loading={isLoading}
				error={isError}
				onRetry={() => void refetch()}
				getRowId={c => c.id ?? c.year ?? 0}
				columns={columns}
				searchPredicate={(c, q) => String(c.year ?? '').includes(q)}
				defaultSort={{ key: 'year', dir: 'desc' }}
				emptyIcon={<CalendarClock size={36} className="text-foreground-disabled" strokeWidth={1.25} />}
				addLabel={t('system.availableDates.add')}
				onAdd={() => {
					setEditing(null);
					setEditOpen(true);
				}}
				onEdit={c => {
					setEditing(calendarToForm(c));
					setEditOpen(true);
				}}
				onDelete={setDeleteRow}
				rowActions={c => (
					<>
						<Button
							variant="ghost"
							size="icon"
							type="button"
							aria-label={t('system.availableDates.manageWorkDays')}
							title={t('system.availableDates.manageWorkDays')}
							disabled={!c.id}
							onClick={() => setWorkDaysFor(c)}>
							<CalendarDays size={15} />
						</Button>
						<Button
							variant="ghost"
							size="icon"
							type="button"
							aria-label={t('system.availableDates.manageExceptions')}
							title={t('system.availableDates.manageExceptions')}
							disabled={!c.id}
							onClick={() => setExceptionsFor(c)}>
							<CalendarX2 size={15} />
						</Button>
					</>
				)}
				exportFilename="available-dates"
			/>

			<WorkCalendarEditDialog open={editOpen} onOpenChange={setEditOpen} initial={editing} />

			{workDaysFor?.id != null && (
				<WorkDaysSheet
					open={Boolean(workDaysFor)}
					onOpenChange={o => !o && setWorkDaysFor(null)}
					wcId={workDaysFor.id}
					year={workDaysFor.year ?? 0}
				/>
			)}
			{exceptionsFor?.id != null && (
				<ExceptionDaysSheet
					open={Boolean(exceptionsFor)}
					onOpenChange={o => !o && setExceptionsFor(null)}
					wcId={exceptionsFor.id}
					year={exceptionsFor.year ?? 0}
				/>
			)}

			<Dialog open={Boolean(deleteRow)} onOpenChange={open => !open && setDeleteRow(null)}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t('system.availableDates.deleteTitle')}</DialogTitle>
						<DialogDescription>{t('system.availableDates.deleteBody', { year: deleteRow?.year ?? '' })}</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="ghost" onClick={() => setDeleteRow(null)}>
							{t('common.cancel')}
						</Button>
						<Button variant="destructive" onClick={confirmDelete} disabled={remove.isPending}>
							{t('common.delete')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}
